import React, { useState, useEffect, useCallback, useRef } from "react";
import Input from "../../common/input";
import Select from "../../common/select";
import Modal from "../../common/modal";
import { OrderFormData, Address, UpdateOrder } from "../types";
import { useSepomexService } from "../hooks/use-sepomex-service";
import { useOrderService } from "../hooks/use-order-service";
import { useFileUpload } from "../hooks";
import { Button } from 'primereact/button';
import { DropdownChangeEvent } from "primereact/dropdown";
import { cfdiUseOptions, paymentMethodOptions, taxRegimeOptions } from "../constants/billing-options";
import { regionOptions } from "../types";
import "./order-form.css";

const sanitizeAddressField = (value: string): string =>
  value.replace(/[^a-zA-Z0-9\sñÑáéíóúÁÉÍÓÚ´,]/g, "");

interface OrderFormProps {
  shippingAddress?: Address;
  shopifyOrderId?: string;
  cfdi_use?: string | null;
  payment_method?: string | null;
  tax_regime?: string | null;
}

const OrderForm: React.FC<OrderFormProps> = ({
  shippingAddress,
  shopifyOrderId,
  cfdi_use,
  payment_method,
  tax_regime,
}) => {
  const [formData, setFormData] = useState<OrderFormData>({
    state: "",
    district: "",
    neighborhood: "",
    fullName: "",
    street: "",
    zipCode: "",
    number: "",
    cmpNumber: "",
    region: "",
  });

  const [wantsBilling, setWantsBilling] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [cfdiUse, setCfdiUse] = useState<string>(cfdi_use || "");
  const [paymentMethod, setPaymentMethod] = useState<string>(payment_method || "");
  const [taxRegime, setTaxRegime] = useState<string>(tax_regime || "");
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [rfc, setRfc] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState<string>(
    "¿Está seguro que su domicilio es correcto? En cuanto confirme, su pedido será procesado para iniciar el envío."
  );
  const [modalTitle, setModalTitle] = useState<string>("Confirmar Dirección");
  const [modalType, setModalType] = useState<"confirmation" | "notification">(
    "confirmation"
  );

  // Hooks personalizados
  const { 
    fetchLocationByZipCode, 
    settlements, 
    loading: loadingLocation, 
    error: locationError 
  } = useSepomexService();
  
  const { 
    updateOrder, 
    loading: updatingOrder, 
    error: updateError 
  } = useOrderService();

  const { 
    uploadFile, 
    loading: uploadingFile, 
    error: uploadError 
  } = useFileUpload();

  // Función para encontrar la región correspondiente basándose en el nombre del estado
  const findRegionByStateName = useCallback((stateName: string): string => {
    if (!stateName) return "";
    
    const matchingRegion = regionOptions.find(option => {
      return option.label === stateName;
    });
    
    return matchingRegion?.value || "";
  }, []);

  // Función para obtener los datos de ubicación basados en el código postal
  const fetchLocationData = useCallback(async (zipCode: string) => {
    if (!zipCode || zipCode.length !== 5 || !/^\d+$/.test(zipCode)) {
      return;
    }

    try {
      const data = await fetchLocationByZipCode(zipCode);
      
      if (data) {
        // Encontrar la región correspondiente al estado
        const matchingRegion = findRegionByStateName(data.state);
        
        setFormData((prevData) => ({
          ...prevData,
          state: data.state,
          district: data.city,
          region: matchingRegion,
        }));

        // Si solo hay una colonia, la seleccionamos automáticamente
        if (data.settlements && data.settlements.length === 1) {
          setFormData((prevData) => ({
            ...prevData,
            settlement: data.settlements[0].value,
            address2: data.settlements[0].value,
          }));
        }
      }
    } catch (err) {
      console.error("Error al obtener datos del código postal:", err);
    }
  }, [fetchLocationByZipCode, findRegionByStateName]);

  // Efecto para cargar datos iniciales cuando se recibe la dirección de envío
  useEffect(() => {
    if (shippingAddress) {
      const matchingRegion = findRegionByStateName(shippingAddress.province);
      
      setFormData({
        state: shippingAddress.province,
        district: shippingAddress.city,
        neighborhood: "",
        fullName: shippingAddress.firstName + " " + shippingAddress.lastName,
        street: sanitizeAddressField(shippingAddress.address1 || ""),
        zipCode: shippingAddress.zip,
        region: matchingRegion,
      });

      // Si ya hay un código postal, obtenemos la información
      if (shippingAddress.zip) {
        fetchLocationData(shippingAddress.zip);
      }
    }
  }, [shippingAddress, fetchLocationData, findRegionByStateName]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    
    // Filtrar caracteres especiales para street, number y cmpNumber
    let filteredValue = value;
    if (id === "street" || id === "number" || id === "cmpNumber") {
      filteredValue = sanitizeAddressField(value);
    }
    
    setFormData(prevData => ({
      ...prevData,
      [id]: filteredValue,
    }));

    // Si el campo que cambió es el código postal
    if (id === "zipCode") {
      // Si el código postal está completo (5 dígitos), consultamos la API
      if (filteredValue.length === 5 && /^\d+$/.test(filteredValue)) {
        fetchLocationData(filteredValue);
      } else {
        // Si no está completo, limpiamos los datos de ubicación
        setFormData(prevData => ({
          ...prevData,
          state: "",
          district: "",
          neighborhood: "",
          address2: "",
          region: "",
        }));
      }
    }
  }, [fetchLocationData]);

  const handleSelectChange = useCallback((value: string, field: string) => {
    setFormData(prevData => ({
      ...prevData,
      [field]: value,
      // Si es neighborhood, también actualizar address2
      ...(field === 'neighborhood' && { address2: value }),
    }));
  }, []);

  // Función para verificar si el formulario está completo
  const isFormValid = useCallback(() => {
    const baseFormValid = (
      formData.zipCode?.length === 5 &&
      formData.state?.trim() !== "" &&
      formData.district?.trim() !== "" &&
      formData.neighborhood?.trim() !== "" &&
      formData.street?.trim() !== "" &&
      formData.number?.trim() !== ""
    );

    // Si quiere facturación, validar también los campos de facturación
    if (wantsBilling) {
      return baseFormValid && 
             selectedFile !== null && 
             rfc.trim() !== "" &&
             cfdiUse.trim() !== "" && 
             paymentMethod.trim() !== "" &&
             taxRegime.trim() !== "";
    }

    // Si no quiere facturación, solo validar el formulario base
    return baseFormValid;
  }, [formData, wantsBilling, selectedFile, cfdiUse, paymentMethod, taxRegime, rfc]);

  const handleOpenConfirmModal = useCallback(() => {
    if (!isFormValid()) {
      return;
    }
    
    setModalTitle("Confirmar Dirección");
    setModalMessage(
      "¿Está seguro que su domicilio es correcto? En cuanto confirme, su pedido será procesado para iniciar el envío."
    );
    setModalType("confirmation");
    setIsModalOpen(true);
  }, [isFormValid]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    handleOpenConfirmModal();
  }, [handleOpenConfirmModal]);

  const handleConfirm = useCallback(async () => {
    // Validar que tengamos el ID de la orden
    if (!shopifyOrderId) {
      setModalTitle("Error");
      setModalMessage(
        "No se pudo actualizar la dirección: ID de la orden no disponible."
      );
      setModalType("notification");
      setIsModalOpen(true);
      return;
    }

    try {
      // Solo subir archivo si quiere facturación y tiene todos los datos necesarios
      if (wantsBilling && selectedFile && cfdiUse && paymentMethod && taxRegime && rfc) {
        await uploadFile(shopifyOrderId, selectedFile, cfdiUse, paymentMethod, taxRegime, rfc);
      }

      // Preparar la dirección para la actualización
      const updatedAddress: UpdateOrder = {
        fullName: formData.fullName || "",
        street: formData.street || "",
        district: formData.district,
        state: formData.state,
        zipCode: formData.zipCode || "",
        number: formData.number || "",
        region: formData.region,
        neighborhood: formData.neighborhood || "",
      };

      if (formData.cmpNumber) {
        updatedAddress.cmpNumber = formData.cmpNumber;
      }

      // Llamar al servicio para actualizar la orden
      await updateOrder(shopifyOrderId, updatedAddress);
      
      // Actualizar la página después de una actualización exitosa
      window.location.reload();
      
    } catch (error) {
      // Mostrar mensaje de error en caso de fallo
      setModalTitle("Error en la Actualización");

      if (error instanceof Error) {
        setModalMessage(`No se pudo actualizar la dirección: ${error.message}`);
      } else {
        setModalMessage(
          "Ocurrió un error inesperado al actualizar la dirección de envío."
        );
      }

      setModalType("notification");
      setIsModalOpen(true);
      console.error("Error al actualizar la dirección:", error);
    }
  }, [formData, shopifyOrderId, updateOrder, wantsBilling, selectedFile, cfdiUse, paymentMethod, taxRegime, rfc, uploadFile]);

  // Función para manejar cuando el input pierde el foco
  const handleZipBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Validar que tenga 5 dígitos cuando pierde el foco
    if (value && value.length < 5) {
      setFormData((prevData) => ({
        ...prevData,
        state: "",
        district: "",
        neighborhood: "",
        address2: "",
        region: "",
      }));
    }
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  }, []);

  const handleCfdiUseChange = useCallback((e: DropdownChangeEvent) => {
    setCfdiUse(e.value);
  }, []);

  const handlePaymentMethodChange = useCallback((e: DropdownChangeEvent) => {
    setPaymentMethod(e.value);
  }, []);

  const handleTaxRegimeChange = useCallback((e: DropdownChangeEvent) => {
    setTaxRegime(e.value);
  }, []);

  const handleRfcChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRfc(e.target.value);
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type === "application/pdf") {
        setSelectedFile(file);
        if (fileInputRef.current) {
          fileInputRef.current.files = e.dataTransfer.files;
        }
      } else {
        setModalTitle("Error");
        setModalMessage("Solo se permiten archivos PDF");
        setIsModalOpen(true);
      }
    }
  }, []);

  const triggerFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Combinamos los errores de todos los hooks
  const error = locationError || updateError || uploadError;
  const isLoading = loadingLocation || updatingOrder || uploadingFile;

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} noValidate>
        <h2>Dirección del Pedido</h2>

        <Input
          type="text"
          id="zipCode"
          value={formData.zipCode || ""}
          onChange={handleInputChange}
          onBlur={handleZipBlur}
          label="Código Postal"
          required
        />

        {error && <div className="error-message">{error}</div>}

        <Input
          type="text"
          id="state"
          value={formData.state}
          onChange={handleInputChange}
          label="Estado"
          disabled
        />

        <Input
          type="text"
          id="district"
          value={formData.district}
          onChange={handleInputChange}
          label="Distrito"
          disabled
        />

        <Select
          id="region"
          options={regionOptions}
          value={formData.region}
          onChange={(event) => handleSelectChange(event.value, 'region')}
          label="Región"
          disabled
        />

        <Select
          id="neighborhood"
          options={settlements}
          value={formData.neighborhood}
          onChange={(event) => handleSelectChange(event.value, 'neighborhood')}
          label="Colonia"
          required
        />

        <p className="street-field-notice">
          No se aceptan caracteres especiales como <strong>#</strong>, <strong>&</strong>,{" "}
          <strong>$</strong>, <strong>.</strong>, <strong>-</strong> ni <strong>/</strong>.
          No uses números en el campo de calle; escríbelos en{" "}
          <strong>Número del domicilio</strong> o en <strong>Número complemento</strong>.
          <br />
          <span className="street-field-notice__examples">
            Ejemplo calle: <em>Av Reforma</em>, <em>Juárez</em>. Ejemplo número: <em>12</em>,{" "}
            <em>45A</em>, <em>SN</em>.
          </span>
        </p>

        <Input
          type="text"
          id="street"
          value={formData.street || ""}
          onChange={handleInputChange}
          label="Calle"
          description="Introduce únicamente el nombre de la calle"
          required
        />

        <div style={{ display: "flex", gap: "14px" }}>
          <Input
            type="text"
            id="number"
            value={formData.number || ""}
            onChange={handleInputChange}
            label="Número del domicilio"
            description="Introduce el número del domicilio o especifica SN"
            required
          />

          <Input
            type="text"
            id="cmpNumber"
            value={formData.cmpNumber || ""}
            onChange={handleInputChange}
            label="Número complemento"
            description="Número complemento ejemplo: INT 2, opcional"
          />
        </div>

        {/* Checkbox para facturación */}
        <div className="billing-checkbox">
          <label>
            <input
              type="checkbox"
              checked={wantsBilling}
              onChange={(e) => setWantsBilling(e.target.checked)}
            />
            ¿Deseas facturar tu compra? este es el único momento para hacerlo.
          </label>
        </div>

        {/* Campos de facturación */}
        {wantsBilling && (
          <div className="billing-fields">
            <h3>Facturación</h3>
            <p className="billing-info">
              Para continuar, debes subir tu constancia fiscal y seleccionar el uso de CFDI y método de pago.
            </p>
            
            {/* Input de archivo oculto */}
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              ref={fileInputRef}
              className="fileInput"
            />

            {/* Área de arrastrar y soltar */}
            <div
              onClick={triggerFileInput}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`dropZone ${isDragging ? "dropZoneActive" : ""}`}
            >
              <div className="fileIcon">📄</div>

              {selectedFile ? (
                <div>
                  <p className="fileName">{selectedFile.name}</p>
                  <p className="fileSize">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </p>
                </div>
              ) : (
                <>
                  <p className="dropZoneText">
                    Arrastra tu archivo PDF aquí o haz clic para seleccionarlo
                  </p>
                  <p className="dropZoneSubtext">Solo archivos PDF</p>
                </>
              )}
            </div>

            <Input
              type="text"
              id="rfc"
              value={rfc}
              onChange={handleRfcChange}
              label="RFC"
              description="Introduce tu RFC"
              required
            />

            <Select
              options={cfdiUseOptions}
              value={cfdiUse}
              onChange={handleCfdiUseChange}
              label="Uso de CFDI"
              id="cfdi-use"
              required={wantsBilling}
            />
            
            <Select
              options={paymentMethodOptions}
              value={paymentMethod}
              onChange={handlePaymentMethodChange}
              label="Forma de Pago"
              id="payment-method"
              required={wantsBilling}
            />
             
            <Select
              options={taxRegimeOptions}
              value={taxRegime}
              onChange={handleTaxRegimeChange}
              label="Régimen Fiscal"
              id="tax-regime"
              required={wantsBilling}
            />
          </div>
        )}

        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <Button
            type="submit"
            onClick={() => {}}
            disabled={isLoading}
            label={isLoading ? "Cargando..." : "Confirmar"}
            className="dark-button"
          />
        </div>
      </form>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirm}
        title={modalTitle}
        message={modalMessage}
        isLoading={updatingOrder || uploadingFile}
        type={modalType}
      />
    </div>
  );
};

export default OrderForm;
